import TransactionHeaderMobile from '@assets/images/background/transaction-header-mobile.svg';
import TransactionHeaderTablet from '@assets/images/background/transaction-header-tablet.svg';
import TransactionHeaderDesktop from '@assets/images/background/transaction-header-desktop.svg';
import { ITransaction, ITransactions } from '@interfaces/transaction';
import { TRANSACTION_CATEGORY } from '@constants/transaction.constants';
import { IRedemption } from '@interfaces/redemption';
import { TransactionTemplate } from '@components/TransactionCard/TransactionTemplate';
import { RedemptionTemplate } from '@components/TransactionCard/RedemptionTemplate';

interface Props {
	transactionGroup: ITransactions<ITransaction | IRedemption>;
	category?: TRANSACTION_CATEGORY;
	isFirst?: boolean;
	className?: string;
	timeRange?: { start: Date; end: Date };
};

export default function TransactionCard({ transactionGroup, isFirst, className, category = TRANSACTION_CATEGORY.TRANSACTION, timeRange }: Props) {
	const formatTime = (date: Date) => {
		const hours = date.getHours();
		const minutes = date.getMinutes();
		const ampm = hours >= 12 ? 'pm' : 'am';
		const displayHours = hours % 12 || 12;
		const displayMinutes = minutes.toString().padStart(2, '0');
		return `${displayHours}:${displayMinutes} ${ampm}`;
	};

	const isSameDay = (date1: Date, date2: Date) => {
		return date1.getDate() === date2.getDate() && date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear();
	};

	const getTimeRangeDisplay = () => {
		if (!timeRange) {
			return '12:00 am - 11:59 pm';
		}

		// Parse the transaction group date (e.g., "01 Sep 2024")
		const groupDate = new Date(transactionGroup.createdAt);
		const startDate = new Date(timeRange.start);
		const endDate = new Date(timeRange.end);

		const isFirstDay = isSameDay(groupDate, startDate);
		const isLastDay = isSameDay(groupDate, endDate);

		if (isFirstDay && isLastDay) {
			// Same day range
			return `${formatTime(timeRange.start)} - ${formatTime(timeRange.end)}`;
		}
		if (isFirstDay) {
			// First day of range
			return `${formatTime(timeRange.start)} - 11:59 pm`;
		}
		if (isLastDay) {
			// Last day of range
			return `12:00 am - ${formatTime(timeRange.end)}`;
		}
		// Middle days
		return '12:00 am - 11:59 pm';
	};

	const getTemplate = (category: TRANSACTION_CATEGORY, transactions: (ITransaction | IRedemption)[]) => {
		switch (category) {
			case TRANSACTION_CATEGORY.REDEMPTION:
				return (
					<>
						{transactions.map((transaction) => (
							<RedemptionTemplate redemption={transaction as IRedemption} key={(transaction as IRedemption).redemptionId} />
						))}
					</>
				);
			default:
				return (
					<>
						{transactions.map((transaction) => (
							<TransactionTemplate transaction={transaction as ITransaction} key={transaction._id} />
						))}
					</>
				);
		}
	};

	return (
		<div className='relative'>
			{isFirst && (
				<>
					<div className='tablet:hidden absolute left-[-24px] top-0 w-[calc(100%+48px)] max-w-[calc(100%+48px)] h-auto object-cover rounded-t-lg'>
						<TransactionHeaderMobile />
					</div>
					<div className='hidden tablet:block desktop:hidden absolute left-[-24px] top-0 w-[calc(100%+48px)] max-w-[calc(100%+48px)] h-auto object-cover rounded-t-lg'>
						<TransactionHeaderTablet />
					</div>
					<div className='hidden desktop:block absolute left-[-24px] top-0 w-[calc(100%+48px)] max-w-[calc(100%+48px)] h-[218px] object-cover rounded-t-lg'>
						<TransactionHeaderDesktop />
					</div>
				</>
			)}
			<div className={`flex justify-between ${className ?? 'bg-black'} rounded-t-lg text-white pt-4 pb-6 ${isFirst ? '' : 'px-6'}`}>
				<div className='z-10 leading-[18px]'>
					<div className={`font-semibold ${isFirst ? 'text-2xl' : 'text-xl'}`}>{transactionGroup.createdAt}</div>
					<div>{getTimeRangeDisplay()}</div>
				</div>
				{category === TRANSACTION_CATEGORY.TRANSACTION ? (
					<div className='z-10 text-right leading-[18px]'>
						<div className={`font-semibold ${isFirst ? 'text-2xl' : 'text-xl'}`}>{transactionGroup.amount?.toFixed(2)}</div>
						<p>{(transactionGroup.transactions[0] as ITransaction).currency}</p>
					</div>
				) : (
					<></>
				)}
			</div>
			<div className='bg-white rounded-lg px-6 py-4 relative top-[-8px]'>
				<div className='text-lg font-semibold pb-[8px]'>
					{transactionGroup.transactions.length} {category}
				</div>
				<hr className='border-dashed' />
				<div>{getTemplate(category ?? TRANSACTION_CATEGORY.TRANSACTION, transactionGroup.transactions)}</div>
			</div>
		</div>
	);
}
